import {
  autoUpdate,
  flip,
  FloatingOverlay,
  type Placement as FloatingPlacement,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { type Key, type MouseEventHandler, type ReactNode, useState } from "react";

import { ReactComponent as ChevronLeft } from "atoms/icons/chevron_left.svg";
import { cn } from "@/shadcn/lib/utils";
import { hasProperty } from "toolbox/Guards";
import { delay } from "toolbox/Timeout";

type OffsetCorrection = {
  x?: number;
  y?: number;
};

type MenuOptionSuboptions = {
  subOptions?: MenuOption[];
};

type MenuOptionClickable = {
  onClick: MouseEventHandler<HTMLButtonElement>;
};

type MenuOptionsItem = {
  id: Key;
  label: string | JSX.Element;
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
};

type MenuOptionsSeparator = {
  separator: true;
};

export type MenuOption =
  | MenuOptionsSeparator
  | MenuOptionsItem
  | (MenuOptionsItem & MenuOptionSuboptions)
  | (MenuOptionsItem & MenuOptionClickable);

type Placement = "bottom-end" | "bottom-start";

type MenuProps = {
  options: MenuOption[];
  isSubmenu?: boolean;
  placement?: Placement;
  offsetCorrection?: OffsetCorrection;
  clickDelay?: number;
  itemsClassName?: string;
  buttonClassName?: string;
  disabled?: boolean;
  trigger: (open: boolean) => ReactNode;
  onClose?: () => void;
};

const ConditionalFloatingPortal = ({
  isSubmenu,
  children,
}: {
  isSubmenu: boolean;
  children: JSX.Element;
}) => {
  if (isSubmenu) {
    return children;
  }

  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="z-120">
        {children}
      </FloatingOverlay>
    </FloatingPortal>
  );
};

const submenuPosition: Record<Placement, FloatingPlacement> = {
  "bottom-start": "right-start",
  "bottom-end": "left-start",
};
const MENU_ITEM_CLASS_NAME = "w-full whitespace-nowrap px-4 py-4 text-left";

export const Menu = ({
  options,
  isSubmenu = false,
  itemsClassName = "",
  buttonClassName = "",
  clickDelay,
  offsetCorrection,
  disabled = false,
  placement = "bottom-end",
  trigger,
  onClose,
}: MenuProps) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const menuPlacement = isSubmenu ? submenuPosition[placement] : placement;

  const {
    floatingStyles,
    refs: { setReference, setFloating },
    context,
  } = useFloating<HTMLButtonElement>({
    open: isMenuOpen,
    onOpenChange: setMenuOpen,
    placement: menuPlacement,
    middleware: [
      offset({
        mainAxis: isSubmenu ? 0 : (offsetCorrection?.x ?? 0),
        alignmentAxis: isSubmenu ? 0 : (offsetCorrection?.y ?? 0),
      }),
      shift(),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: isSubmenu,
    handleClose: safePolygon({ blockPointerEvents: true }),
  });
  const click = useClick(context, {
    event: "mousedown",
    enabled: false,
    toggle: !isSubmenu,
    ignoreMouse: isSubmenu,
  });
  const role = useRole(context, { role: "menu" });
  const dismiss = useDismiss(context, { bubbles: true });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, role, dismiss]);

  const isSubmenuOption = (option: MenuOption): option is MenuOptionsItem & MenuOptionSuboptions =>
    hasProperty(option, "subOptions");

  const isClickOption = (option: MenuOption): option is MenuOptionsItem & MenuOptionClickable =>
    hasProperty(option, "onClick");

  const isDisplayOption = (option: MenuOption): option is MenuOptionsItem =>
    !hasProperty(option, "onClick") &&
    !hasProperty(option, "subOptions") &&
    hasProperty(option, "label");

  const isSeparatorOption = (option: MenuOption): option is MenuOptionsSeparator =>
    hasProperty(option, "separator");

  const getOptionId = (option: MenuOption, index: number) =>
    hasProperty(option, "id") ? (option as MenuOptionsItem).id : `separator-${index}`;

  const handleClose = () => {
    onClose?.();
    setMenuOpen(false);
  };

  const handleClickItem = (event: React.MouseEvent<HTMLButtonElement>, option: MenuOption) => {
    event.stopPropagation();
    event.preventDefault();

    if (disabled || (hasProperty(option, "disabled") && (option as MenuOptionsItem).disabled)) {
      return;
    }

    if (isClickOption(option)) {
      option.onClick(event);
    }

    handleClose();
  };

  const handleTriggerClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    event.stopPropagation();
    await delay(clickDelay ?? 0);
    setMenuOpen(!isMenuOpen);
  };

  return (
    <div data-testid="menu">
      <button
        aria-disabled={disabled}
        className={cn(
          !disabled && "cursor-pointer",
          disabled && "cursor-not-allowed",
          buttonClassName,
        )}
        ref={setReference}
        type="button"
        {...getReferenceProps()}
        onClick={handleTriggerClick}
      >
        {trigger(isMenuOpen)}
      </button>
      {isMenuOpen && (
        <ConditionalFloatingPortal isSubmenu={isSubmenu}>
          <div
            className={cn(
              "absolute mt-1 flex flex-col rounded-md bg-surface py-1 type-body-md text-content shadow-subtle",
              itemsClassName,
            )}
            data-testid="menu-items"
            ref={setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {options.map((option, index) => {
              const optionId = getOptionId(option, index);
              const itemOption = option as MenuOptionsItem;

              return (
                <div
                  className={cn(
                    (isClickOption(option) || isSubmenuOption(option)) && "hover:bg-surface-muted",
                    hasProperty(option, "hidden") && itemOption.hidden && "hidden",
                  )}
                  data-testid={`menuItem-${optionId}`}
                  key={optionId}
                >
                  {isSeparatorOption(option) && <hr className="border-border-token" />}
                  {/* aria-disabled rather than the native attribute: a disabled item stays focusable,
                      so a keyboard user can reach it and hear that it is unavailable instead of never
                      finding it. `handleClickItem` refuses the activation. */}
                  {(isClickOption(option) || isDisplayOption(option)) && (
                    <button
                      aria-disabled={option.disabled}
                      className={cn(
                        MENU_ITEM_CLASS_NAME,
                        option.disabled && "cursor-not-allowed text-muted-foreground opacity-50",
                        option.className,
                      )}
                      data-testid={`menu-item-${index}`}
                      type="button"
                      onClick={(event) => handleClickItem(event, option)}
                    >
                      {option.label}
                    </button>
                  )}
                  {isSubmenuOption(option) && (
                    <Menu
                      isSubmenu
                      buttonClassName={MENU_ITEM_CLASS_NAME}
                      options={option.subOptions ?? []}
                      placement={placement}
                      trigger={() => (
                        <div className="flex items-center justify-between gap-4">
                          <ChevronLeft
                            className={cn(
                              "h-4 w-4",
                              placement === "bottom-start" ? "order-2 rotate-180" : "order-1",
                              option.className,
                            )}
                          />
                          <span className={placement === "bottom-start" ? "order-1" : "order-2"}>
                            {option.label}
                          </span>
                        </div>
                      )}
                      onClose={handleClose}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </ConditionalFloatingPortal>
      )}
    </div>
  );
};
